// import { loadJsHecateScript } from '@spot-fe/app-bridge';
import {
  Project,
  ProjectDetailV2,
  UserProjectDataV2,
  TokenAmount,
  RewardStatus,
  TaskType,
  JoinType,
} from '@/apis';
import {
  eq,
  gte,
  gt,
  div,
  times,
  plus,
  minus,
  min,
  max,
  safeToFormat,
} from '@/utils/number';
import { isApp, isBrowser, loadJsHecateScript } from '@spot-fe/utils';
import { uniq } from '@/utils';
import { useData } from './[lang]/trade/spot/token-splash/detail/store';
import { Task } from '@/components/TaskCard/panel';

// duration
export enum Duration {
  Default = 'default',
  Apply = 'apply',
  // ApplyEnd = 'applyEnd',
  Deposit = 'deposit',
  DepositEnd = 'depositEnd',
  Publish = 'publish',
  Open = 'open',
  End = 'end',
}

export const getDurationFromProject = (project?: Project) => {
  if (!project) {
    return;
  }
  // end
  if (Date.now() > project.publishTime) {
    return Duration.End;
  }
  // publish
  if (Date.now() > project.publishTime) {
    return Duration.Publish;
  }
  // deposit end
  if (Date.now() > project.depositEnd) {
    return Duration.DepositEnd;
  }
  // deposit
  if (Date.now() > project.depositStart) {
    return Duration.Deposit;
  }
  // apply end
  // if(Date.now() > project.applyEnd) {
  //   return Duration.ApplyEnd
  // }
  // apply
  if (Date.now() > project.applyStart) {
    return Duration.Apply;
  }
  // default
  return Duration.Default;
};

export const getDurationFromDetail = (detail?: ProjectDetailV2, type?: TaskType) => {
  if (!detail) {
    return;
  }
  if (type === TaskType.Deposit) {
    // end
    if (Date.now() > detail.publishTime) {
      return Duration.End;
    }
    // deposit end
    if (Date.now() > detail.depositEnd) {
      return Duration.DepositEnd;
    }
    // deposit
    if (Date.now() > detail.depositStart) {
      return Duration.Deposit;
    }
    // apply
    if (Date.now() > detail.applyStart) {
      return Duration.Apply;
    }
    // default
    return Duration.Default;
  } else {
    // end
    if (Date.now() > detail.tradePublishTime) {
      return Duration.End
    }
    // trade end
    if (Date.now() > detail.tradeEnd) {
      return Duration.DepositEnd;
    }
    // trade
    if (Date.now() > detail.tradeStart) {
      return Duration.Deposit;
    }
    // apply
    if (Date.now() > detail.tradeApplyStart) {
      return Duration.Apply;
    }
    // default
    return Duration.Default;
  }
};

const openAppLogin = async (isRegister?: boolean) => {
  const registerParams = {
    path: 'register',
    fromWeb: '1', // 注册完跳回原来的页面
    directlyRegister: true,
  };
  try {
    await loadJsHecateScript();
    (window as any)?.BybitHecate?.module('UserProfile')
      .method('getUserProfile')
      .invoke({
        needLogin: true,
        ...(isRegister ? registerParams : {}),
      })
      .then((res) => {
        // 当前页面刷新
        // modalOptionRef.current?.onLoginSuccessCb?.();
        setTimeout(() => {
          window.location.reload();
        }, 500);
      })
      .catch(() => { });
  } catch (error) {
    console.error(error);
  }
};

export const navToLogin = async () => {
  // 判断app
  if (isApp()) {
    await openAppLogin();
    return;
  }
  const origin = location.href;
  const link = `/login?redirect_url=${encodeURIComponent(origin)}`;
  if (isBrowser) {
    window.location.href = link;
  }
};

export const taskComplete = (
  detail?: ProjectDetailV2,
  userData?: UserProjectDataV2
) => {
  const { allFinished } = getTaskCompleteStatus(detail, userData);
  return allFinished;
};

interface Item extends TokenAmount {
  percent: number;
}

const getMaxData = (list: Item[]) => {
  let max;
  list.forEach((item) => {
    if (max == null) {
      max = item;
    } else {
      max = gt(item.percent, max.percent) ? item : max;
    }
  });
  return max;
};

interface CompleteItem extends TokenAmount {
  percent: number;
  diffAmount: string;
  depositAmount: string;
}

export const getTokens = (tokens: TokenAmount[]) => {
  return (tokens || [])
    .map((item) => {
      return `${safeToFormat(item.amount, 0)} ${item.token}`;
    })
    .join(', ');
};

export const getTokensExp = (tokens: TokenAmount[]) => {
  return (tokens || [])
    .map((item) => {
      return `${item.amount} ${item.token}`;
    })
    .join(', ');
};

export const getTokensRemain = (tokens: CompleteItem[]) => {
  return (tokens || [])
    .map((item) => {
      // 差额向上取整
      return `${safeToFormat(max([0, item.diffAmount]), 0, 0)} ${item.token}`;
    })
    .join(', ');
};

export const getTokensProgress = (tokens: CompleteItem[]) => {
  return (tokens || [])
    .map((item) => {
      const isZero = eq(item.depositAmount || 0, 0);
      if (isZero) {
        return `${safeToFormat(item.amount, 0)} ${item.token}`;
      }
      return `${safeToFormat(item.depositAmount, 0)}/${safeToFormat(
        item.amount,
        0
      )} ${item.token}`;
    })
    .join(', ');
};

export const getTokensProgressExp = (tokens: CompleteItem[]) => {
  return (tokens || [])
    .map((item) => {
      const isZero = eq(item.depositAmount || 0, 0);
      if (isZero) {
        return `${item.amount} ${item.token}`;
      }
      const num = Math.floor(+item.depositAmount * 100000) / 100000
      return `${num}/${item.amount} ${item.token}`;
    })
    .join(', ');
};

export const getTokenNames = (tokens: TokenAmount[]) => {
  const uniqTokens = uniq(
    (tokens || []).map((item) => {
      return item.token;
    })
  );

  return uniqTokens.join(', ');
};

export const getTask1Status = (
  detail?: ProjectDetailV2,
  userData?: UserProjectDataV2
) => {
  const isNewUser = userData?.isNewUser;
  const condition1 = isNewUser
    ? detail?.newUserCondition1
    : detail?.oldUserCondition1;

  const completeList: CompleteItem[] = (condition1 || []).map((item) => {
    const token = userData?.firstDeposit?.find(
      (token) => token.token === item.token
    );
    const depositAmount = token?.amount || '0';
    const diffAmount = minus(item.amount, token?.amount || 0);
    const percent = gt(item.amount, 0)
      ? div(token?.amount || 0, item.amount)
      : gt(token?.amount, 0)
        ? 1
        : 0;
    return { ...item, depositAmount, diffAmount, percent };
  });

  const task1Finished = completeList.some((item) => gte(item.percent, 1));
  const maxItem = getMaxData(completeList);
  const task1Percent = maxItem?.percent || 0;
  const task1Started = gt(task1Percent, 0);

  const multiNewUserToken = (detail?.newUserCondition1?.length || 0) > 1;
  const multiOldUserToken = (detail?.oldUserCondition1?.length || 0) > 1;

  const newUserList1 = multiNewUserToken
    ? detail?.newUserCondition1.slice(0, -1)
    : detail?.newUserCondition1;
  const oldUserList1 = multiOldUserToken
    ? detail?.oldUserCondition1.slice(0, -1)
    : detail?.oldUserCondition1;
  const newUserList2 = multiNewUserToken
    ? detail?.newUserCondition1.slice(-1)
    : [];
  const oldUserList2 = multiOldUserToken
    ? detail?.oldUserCondition1.slice(-1)
    : [];

  const newUserContent1 = getTokensExp(newUserList1 || []);
  const oldUserContent1 = getTokensExp(oldUserList1 || []);

  const newUserContent2 = getTokensExp(newUserList2 || []);
  const oldUserContent2 = getTokensExp(oldUserList2 || []);

  const isMulti = isNewUser ? multiNewUserToken : multiOldUserToken;
  const completeList1 = isMulti ? completeList.slice(0, -1) : completeList;
  const completeList2 = isMulti ? completeList.slice(-1) : [];

  const completeContent1 = getTokensRemain(completeList1 || []);
  const completeContent2 = getTokensRemain(completeList2 || []);

  const progressContent1 = getTokensProgressExp(completeList1 || []);
  const progressContent2 = getTokensProgressExp(completeList2 || []);

  return {
    task1Finished,
    task1Percent,
    task1Started,
    newUserContent1,
    oldUserContent1,
    newUserContent2,
    oldUserContent2,
    multiNewUserToken,
    multiOldUserToken,
    currentTask: maxItem,
    completeList,
    completeList1,
    completeList2,
    completeContent1,
    completeContent2,
    progressContent1,
    progressContent2,
  };
};

export const getTask2Status = (
  detail?: ProjectDetailV2,
  userData?: UserProjectDataV2
) => {
  const isNewUser = userData?.isNewUser;
  const condition2 = isNewUser
    ? detail?.newUserCondition2
    : detail?.oldUserCondition2;

  const depositPercent = min([
    0.5,
    times(
      0.5,
      div(userData?.secondDepositAmount || 0, condition2?.depositAmount)
    ),
  ]);

  const completeList = (condition2?.tradeList || []).map((item) => {
    const token = userData?.secondTrade?.find(
      (token) => token.token === item.token
    );
    const depositAmount = token?.amount || '0';
    const diffAmount = minus(item.amount, token?.amount || 0);
    const percent = gt(item.amount, 0)
      ? div(token?.amount || 0, item.amount)
      : gt(token?.amount, 0)
        ? 1
        : 0;
    return { ...item, depositAmount, diffAmount, percent };
  });

  const task2Finished = completeList.some(
    (item) => gte(item.percent, 1) || (gt(item.depositAmount, 0) && gte(item.depositAmount, item.amount))
  );
  const maxItem = getMaxData(completeList);
  const task2Percent = plus(
    depositPercent,
    min([0.5, times(0.5, maxItem?.percent || 0)])
  );

  const task2Started = gt(task2Percent, 0);

  const newUserList = detail?.newUserCondition2?.tradeList || [];
  const oldUserList = detail?.oldUserCondition2?.tradeList || [];

  const multiNewUserToken = newUserList.length > 1;
  const multiOldUserToken = oldUserList.length > 1;

  // 当只有一个币种且交易金额为0时;或者多个币种且交易金额都为0时;
  const originNewUserToken =
    (newUserList.length === 1 && eq(newUserList[0].amount, 0)) ||
    newUserList.every((item) => eq(item.amount, 0));

  const originOldUserToken =
    (oldUserList.length === 1 && eq(oldUserList[0].amount, 0)) ||
    oldUserList.every((item) => eq(item.amount, 0));

  const originNewUserTokenNames = newUserList
    .map((item) => item.token)
    .join(', ');

  const originOldUserTokenNames = oldUserList
    .map((item) => item.token)
    .join(', ');

  const newUserList1 = multiNewUserToken
    ? detail?.newUserCondition2?.tradeList.slice(0, -1)
    : detail?.newUserCondition2?.tradeList;

  const oldUserList1 = multiOldUserToken
    ? detail?.oldUserCondition2?.tradeList.slice(0, -1)
    : detail?.oldUserCondition2?.tradeList;

  const newUserList2 = multiNewUserToken
    ? detail?.newUserCondition2?.tradeList.slice(-1)
    : [];

  const oldUserList2 = multiOldUserToken
    ? detail?.oldUserCondition2?.tradeList.slice(-1)
    : [];

  const isMulti = isNewUser ? multiNewUserToken : multiOldUserToken;
  const completeList1 = isMulti ? completeList.slice(0, -1) : completeList;
  const completeList2 = isMulti ? completeList.slice(-1) : [];

  return {
    task2Deposited: gte(depositPercent, 0.5),
    task2Traded: task2Finished,
    depositPercent,
    task2Percent,
    task2Started,
    currentTask: maxItem,
    multiNewUserToken,
    multiOldUserToken,
    newUserList1,
    oldUserList1,
    newUserList2,
    oldUserList2,
    completeList,
    completeList1,
    completeList2,
    originNewUserToken,
    originOldUserToken,
    originNewUserTokenNames,
    originOldUserTokenNames,
  };
};

export const getTaskTradeStatus = (
  detail?: ProjectDetailV2,
  userData?: UserProjectDataV2
) => {
  const userTreadAmount = userData?.tradeAmount;
  const totalTrade = detail?.tradeLimitAmount;
  // 还需要交易折合USDT金额,向上取整
  const needTrade = safeToFormat(minus(totalTrade, userTreadAmount), 0, 0);
  const tradePercent = safeToFormat(min([times(div(userTreadAmount, totalTrade), 100), 100]), 0);
  const isRisk = userData?.tradeRewardStatus === RewardStatus.Risk;

  return {
    userTreadAmount,
    totalTrade,
    tradePercent,
    needTrade,
    isDone: tradePercent >= 100,
    isRisk,
  };
};

export const getCustomUserTokens = (
  tokens: TokenAmount[],
  fn: (token: TokenAmount) => string
) => {
  return (tokens || [])
    .map((item) => {
      const newAmount = safeToFormat(item.amount, 0);
      return fn({ ...item, amount: newAmount });
    })
    .join(', ');
};

export const getTaskCompleteStatus = (
  detail?: ProjectDetailV2,
  userData?: UserProjectDataV2
) => {
  const task2 = getTask2Status(detail, userData);
  const allFinished = userData?.isFirstDone || userData?.isSecondDone;
  const taskTrade = getTaskTradeStatus(detail, userData);

  return {
    allFinished,
    task1Finished: userData?.isFirstDone,
    task2Finished: userData?.isSecondDone,
    task2Deposited: task2.task2Deposited,
    task2Traded: task2.task2Traded,
    taskTradeFinished: taskTrade.isDone,
  };
};

export const afterDeposit = (duration) =>
  duration === Duration.End ||
  duration === Duration.DepositEnd ||
  duration === Duration.Publish;

export const getIsJoin = (type, joinType) => {
  return (
    type === TaskType.Deposit &&
    [JoinType.DepositJoin, JoinType.AllJoin].includes(joinType)
  ) ||
    (
      type === TaskType.Trade &&
      [JoinType.TradeJoin, JoinType.AllJoin].includes(joinType)
    )
};
